<template>
  <div
    v-if="displayRows.length > 0"
    class="blocks-root"
    :style="rootStyle"
  >
    <div
      v-for="(row, idx) in displayRows"
      :key="row.id || idx"
      class="block-card"
      :class="{ 'few-rows': isFewRows }"
      :style="cardStyle"
    >
      <div class="block-header" :style="headerStyle">
        <div class="block-header-labels" :style="headerLabelStyle">
          <span
            v-for="label in counterLabels"
            :key="label.lang"
            class="block-label-line"
            :class="{ 'block-label-ar': label.lang === 'ar' }"
          >{{ label.text }}</span>
        </div>
        <div class="block-counter-badge" :style="counterBadgeStyle">
          {{ row.counter }}
        </div>
      </div>

      <div class="block-body" :style="bodyStyle">
        <div class="block-number-labels" :style="numberLabelStyle">
          <span
            v-for="label in numberLabels"
            :key="label.lang"
            :class="{ 'block-label-ar': label.lang === 'ar' }"
          >{{ label.text }}</span>
        </div>
        <div class="block-number-display" :style="numberDisplayStyle">
          <span class="block-number-value">{{ row.ticket }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "QueueBlocksWidget",
};
</script>

<script setup lang="ts">
import { computed, defineProps } from "vue";

const props = defineProps<{
  queueData: Array<any>;
  queueConfig: any;
  borderRadius: number;
  config: any;
  numberTxt?: string;
  counterTxt?: string;
}>();

const qc = computed(() => props.config?.queueConfig || props.queueConfig || {});

const DEFAULT_COUNTER_LABELS: Record<string, string> = {
  en: "Counter",
  fr: "Guichet",
  ar: "شباك",
};

const DEFAULT_NUMBER_LABELS: Record<string, string> = {
  en: "Number",
  fr: "Numéro",
  ar: "رقم",
};

const headerLanguages = computed(() => {
  const langs = qc.value.headerLanguages;
  return Array.isArray(langs) && langs.length > 0 ? langs : ["fr", "ar"];
});

function getBlockLabel(lang: string, type: "counter" | "number"): string {
  const byLang = qc.value.blocksLabelsByLang;
  if (byLang && typeof byLang === "object" && byLang[lang]?.[type]) {
    return String(byLang[lang][type]);
  }
  const defaults =
    type === "counter" ? DEFAULT_COUNTER_LABELS : DEFAULT_NUMBER_LABELS;
  return defaults[lang] ?? defaults.en;
}

const counterLabels = computed(() =>
  headerLanguages.value.map((lang: string) => ({
    lang,
    text: getBlockLabel(lang, "counter"),
  }))
);

const numberLabels = computed(() =>
  headerLanguages.value.map((lang: string) => ({
    lang,
    text: getBlockLabel(lang, "number"),
  }))
);

const widgetWidth = computed(() => props.config?.width || 400);
const widgetHeight = computed(() => props.config?.height || 400);
const rowGap = 12;

const displayRows = computed(() => {
  const data = props.queueData ?? [];
  if (!props.config?.queueConfig?.multipleRows) {
    return data.slice(0, 1);
  }
  return data;
});

const rowCount = computed(() => Math.max(displayRows.value.length, 1));
const isFewRows = computed(() => rowCount.value <= 2);

const availableHeight = computed(() => {
  const gaps = Math.max(0, rowCount.value - 1) * rowGap;
  return widgetHeight.value - gaps;
});

const cardHeight = computed(() => availableHeight.value / rowCount.value);

const borderRadiusCss = computed(() => {
  const r = props.borderRadius ?? props.config?.borderRadius ?? 10;
  return typeof r === "number" ? `${r}px` : String(r);
});

const headerFontSize = computed(() => {
  const raw = qc.value.headerFontSize;
  if (
    raw !== undefined &&
    raw !== null &&
    raw !== 0 &&
    raw !== "0" &&
    raw !== ""
  ) {
    return Number(raw);
  }
  const w = widgetWidth.value;
  const h = cardHeight.value * 0.32;
  return Math.max(14, Math.min(48, Math.min(w * 0.055, h * 0.42)));
});

const contentFontSize = computed(() => {
  const raw = qc.value.contentFontSize;
  if (
    raw !== undefined &&
    raw !== null &&
    raw !== 0 &&
    raw !== "0" &&
    raw !== ""
  ) {
    return Number(raw);
  }
  const w = widgetWidth.value;
  const bodyH = cardHeight.value * 0.68;
  const displayH = bodyH * 0.55;
  return Math.max(22, Math.min(120, Math.min(displayH * 0.55, w * 0.14)));
});

const badgeFontSize = computed(() => {
  const base = contentFontSize.value;
  return Math.max(18, Math.min(base, base * 0.85));
});

const rootStyle = computed(() => ({
  gap: `${rowGap}px`,
}));

const cardStyle = computed(() => ({
  borderColor: qc.value.numberHeaderColor || "#0132ab",
  borderRadius: borderRadiusCss.value,
  minHeight: isFewRows.value ? `${cardHeight.value * 0.45}px` : undefined,
}));

const headerStyle = computed(() => ({
  backgroundColor: qc.value.counterBgColor || "#0032ad",
  borderTopLeftRadius: borderRadiusCss.value,
  borderTopRightRadius: borderRadiusCss.value,
  minHeight: isFewRows.value ? `${cardHeight.value * 0.25}px` : `${Math.max(48, cardHeight.value * 0.32)}px`,
}));

const headerLabelStyle = computed(() => ({
  color: qc.value.counterTxtColor || "#ffffff",
  fontSize: `${headerFontSize.value}px`,
}));

const counterBadgeStyle = computed(() => ({
  color: qc.value.counterTxtColor || "#ffffff",
  borderColor: qc.value.counterTxtColor || "#ffffff",
  fontSize: `${badgeFontSize.value}px`,
  borderRadius: borderRadiusCss.value,
}));

const bodyStyle = computed(() => ({
  backgroundColor: qc.value.numberBgColor || "#ffffff",
  borderBottomLeftRadius: borderRadiusCss.value,
  borderBottomRightRadius: borderRadiusCss.value,
}));

const numberLabelStyle = computed(() => ({
  color: qc.value.numberHeaderColor || "#0132ab",
  fontSize: `${headerFontSize.value}px`,
}));

const numberDisplayStyle = computed(() => ({
  borderColor: qc.value.numberHeaderColor || "#0132ab",
  color: qc.value.numberTxtColor || "#0132ab",
  fontSize: `${contentFontSize.value}px`,
  borderRadius: borderRadiusCss.value,
  backgroundColor: qc.value.numberBgColor || "#ffffff",
}));
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap");

.blocks-root {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.block-card {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 2px solid;
  overflow: hidden;
  box-sizing: border-box;
}

.block-card.few-rows {
  flex: 0 1 auto;
}

.block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0.6em 1em;
  flex: 0 0 auto;
  box-sizing: border-box;
}

.block-header-labels {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  min-width: 0;
}

.block-label-line {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.block-label-ar {
  font-weight: 700;
  direction: rtl;
}

.block-counter-badge {
  flex: 0 0 auto;
  min-width: 2.8em;
  padding: 0.25em 0.65em;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  box-sizing: border-box;
}

.block-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0.5em 1em 0.75em;
  gap: 0.4em;
  box-sizing: border-box;
}

.block-number-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex: 0 0 auto;
  gap: 8px;
}

.block-number-display {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid;
  padding: 0.35em 0.5em;
  box-sizing: border-box;
}

.block-number-value {
  /*font-family: "Share Tech Mono", "Courier New", monospace;*/
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1;
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
